// Technical SEO utilities for security and performance

export interface SecurityHeaders {
  'Content-Security-Policy': string;
  'X-Frame-Options': string;
  'X-Content-Type-Options': string;
  'Referrer-Policy': string;
  'Permissions-Policy': string;
  'Strict-Transport-Security': string;
}

export interface TechnicalSEOConfig {
  enforceHTTPS: boolean;
  securityHeaders: boolean;
  duplicateContentPrevention: boolean;
  crawlBudgetOptimization: boolean;
  canonicalURLs: boolean;
  robotsOptimization: boolean;
}

/**
 * Generate security headers for SEO and security
 */
export const generateSecurityHeaders = (): SecurityHeaders => {
  return {
    'Content-Security-Policy': [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com",
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com",
      "img-src 'self' data: https: http:",
      "connect-src 'self' https://www.google-analytics.com https://analytics.google.com",
      "frame-src 'self' https://www.youtube.com https://www.google.com",
    ].join('; '),
    'X-Frame-Options': 'DENY',
    'X-Content-Type-Options': 'nosniff',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  };
};

/**
 * Enforce HTTPS redirects (skips localhost for development)
 */
export const enforceHTTPS = (): void => {
  if (typeof window !== 'undefined' && window.location.protocol === 'http:') {
    // Skip HTTPS enforcement on localhost for development
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
      return;
    }
    const httpsUrl = window.location.href.replace('http:', 'https:');
    window.location.replace(httpsUrl);
  }
};

/**
 * Prevent duplicate content issues
 */
export const preventDuplicateContent = (canonicalUrl: string): void => {
  // Remove or update existing canonical link
  const existingCanonical = document.querySelector('link[rel="canonical"]');
  if (existingCanonical) {
    existingCanonical.setAttribute('href', canonicalUrl);
  } else {
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = canonicalUrl;
    document.head.appendChild(canonical);
  }

  // Add noindex for duplicate pages
  const urlParams = new URLSearchParams(window.location.search);
  const isDuplicate = urlParams.has('utm_source') || 
                     urlParams.has('ref') || 
                     urlParams.has('fbclid') ||
                     window.location.pathname.endsWith('/');

  if (isDuplicate && window.location.pathname !== '/') {
    const noindexMeta = document.createElement('meta');
    noindexMeta.name = 'robots';
    noindexMeta.content = 'noindex, follow';
    document.head.appendChild(noindexMeta);
  }
};

/**
 * Optimize crawl budget
 */
export const optimizeCrawlBudget = (): {
  optimizations: string[];
  recommendations: string[];
} => {
  const optimizations: string[] = [];
  const recommendations: string[] = [];

  // Block unnecessary URLs in robots.txt
  const unnecessaryPaths = [
    '/admin',
    '/wp-admin',
    '/wp-content',
    '/search',
    '/cart',
    '/checkout',
    '/account',
    '/login',
    '/register',
    '/*?*', // Query parameters
    '/*.pdf$',
    '/*.doc$',
    '/*.xls$',
  ];

  // Add nofollow to pagination beyond page 2
  const paginationLinks = document.querySelectorAll('a[href*="page="], a[href*="/page/"]');
  paginationLinks.forEach((link) => {
    const href = link.getAttribute('href') || '';
    const pageMatch = href.match(/page[=/](\d+)/);
    if (pageMatch && parseInt(pageMatch[1]) > 2) {
      link.setAttribute('rel', 'nofollow');
      optimizations.push(`Added nofollow to pagination link: ${href}`);
    }
  });

  // Optimize internal linking
  const internalLinks = document.querySelectorAll('a[href^="/"], a[href*="' + window.location.hostname + '"]');
  let importantLinksCount = 0;
  
  internalLinks.forEach((link) => {
    const href = link.getAttribute('href') || '';
    
    // Prioritize important pages
    const isImportant = ['/services', '/about', '/contact', '/blog'].some(path => href.startsWith(path));
    if (isImportant) {
      importantLinksCount++;
      // Ensure important links don't have nofollow
      if (link.getAttribute('rel')?.includes('nofollow')) {
        link.setAttribute('rel', link.getAttribute('rel')?.replace('nofollow', '').trim() || '');
        optimizations.push(`Removed nofollow from important link: ${href}`);
      }
    }
  });

  recommendations.push(`Block ${unnecessaryPaths.length} unnecessary paths in robots.txt`);
  recommendations.push(`Prioritize ${importantLinksCount} important internal links`);
  recommendations.push('Use XML sitemaps to guide crawlers to important content');

  return { optimizations, recommendations };
};

/**
 * Generate optimized robots.txt content
 */
export const generateRobotsTxt = (
  siteUrl: string,
  sitemapUrl: string,
  blockedPaths: string[] = []
): string => {
  const defaultBlockedPaths = [
    '/admin',
    '/wp-admin',
    '/wp-content',
    '/search',
    '/cart',
    '/checkout',
    '/account',
    '/login',
    '/register',
    '/api',
    '/*.pdf$',
    '/*.doc$',
    '/*.xls$',
    '/*?*utm_*',
    '/*?*fbclid*',
    '/*?*gclid*',
  ];

  const allBlockedPaths = [...new Set([...defaultBlockedPaths, ...blockedPaths])];

  return `# Robots.txt for ${siteUrl}
# Generated automatically for SEO optimization

User-agent: *
${allBlockedPaths.map(path => `Disallow: ${path}`).join('\n')}

# Allow important crawlers
User-agent: Googlebot
Allow: /

User-agent: Bingbot
Allow: /

# Block bad bots
User-agent: AhrefsBot
Disallow: /

User-agent: MJ12bot
Disallow: /

User-agent: DotBot
Disallow: /

# Crawl delay for aggressive crawlers
User-agent: *
Crawl-delay: 1

# Sitemap location
Sitemap: ${sitemapUrl}

# Host directive (for Google)
Host: ${siteUrl}`;
};

/**
 * Implement automatic canonical URL management
 */
export const autoManageCanonicalURLs = (): void => {
  const currentUrl = window.location.href;
  const cleanUrl = currentUrl.split('?')[0].split('#')[0]; // Remove query params and fragments
  
  // Remove trailing slash except for root
  const canonicalUrl = cleanUrl === window.location.origin + '/' 
    ? cleanUrl 
    : cleanUrl.replace(/\/$/, '');

  preventDuplicateContent(canonicalUrl);
};

/**
 * Optimize meta robots tags automatically
 */
export const optimizeMetaRobots = (): void => {
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  
  // Default robots directive
  let robotsContent = 'index, follow';

  // Noindex for certain pages
  const noindexPaths = ['/search', '/cart', '/checkout', '/account', '/login', '/register', '/admin'];
  const isNoindexPath = noindexPaths.some(noindexPath => path.startsWith(noindexPath));
  
  // Noindex for pages with tracking parameters
  const hasTrackingParams = ['utm_source', 'utm_medium', 'utm_campaign', 'fbclid', 'gclid'].some(param => 
    searchParams.has(param)
  );

  // Noindex for pagination beyond page 1
  const pageParam = searchParams.get('page');
  const isPaginated = pageParam && parseInt(pageParam) > 1;

  if (isNoindexPath || hasTrackingParams || isPaginated) {
    robotsContent = 'noindex, follow';
  }

  // Update or create robots meta tag
  let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
  if (!robotsMeta) {
    robotsMeta = document.createElement('meta');
    robotsMeta.name = 'robots';
    document.head.appendChild(robotsMeta);
  }
  robotsMeta.content = robotsContent;
};

/**
 * Implement structured data validation
 */
export const validateStructuredData = (): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
  
  jsonLdScripts.forEach((script, index) => {
    try {
      const data = JSON.parse(script.textContent || '');
      
      // Basic validation
      if (!data['@context']) {
        errors.push(`Schema ${index + 1}: Missing @context`);
      }
      
      if (!data['@type']) {
        errors.push(`Schema ${index + 1}: Missing @type`);
      }
      
      // Validate common required fields
      if (data['@type'] === 'Organization') {
        if (!data.name) errors.push(`Schema ${index + 1}: Organization missing name`);
        if (!data.url) warnings.push(`Schema ${index + 1}: Organization missing URL`);
      }
      
      if (data['@type'] === 'Article') {
        if (!data.headline) errors.push(`Schema ${index + 1}: Article missing headline`);
        if (!data.author) errors.push(`Schema ${index + 1}: Article missing author`);
        if (!data.datePublished) errors.push(`Schema ${index + 1}: Article missing datePublished`);
      }
      
    } catch (e) {
      errors.push(`Schema ${index + 1}: Invalid JSON`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Auto-fix common technical SEO issues
 */
export const autoFixTechnicalSEO = (): {
  fixed: string[];
  recommendations: string[];
} => {
  const fixed: string[] = [];
  const recommendations: string[] = [];

  // Fix missing meta viewport
  if (!document.querySelector('meta[name="viewport"]')) {
    const viewport = document.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1';
    document.head.appendChild(viewport);
    fixed.push('Added missing viewport meta tag');
  }

  // Fix missing charset
  if (!document.querySelector('meta[charset]')) {
    const charset = document.createElement('meta');
    charset.setAttribute('charset', 'UTF-8');
    document.head.insertBefore(charset, document.head.firstChild);
    fixed.push('Added missing charset meta tag');
  }

  // Fix images without alt text
  const imagesWithoutAlt = document.querySelectorAll('img:not([alt])');
  imagesWithoutAlt.forEach((img, index) => {
    const src = img.getAttribute('src') || '';
    const filename = src.split('/').pop()?.split('.')[0] || `image-${index + 1}`;
    img.setAttribute('alt', filename.replace(/[-_]/g, ' '));
    fixed.push(`Added alt text to image: ${filename}`);
  });

  // Fix links without proper attributes
  const externalLinks = document.querySelectorAll('a[href^="http"]:not([href*="' + window.location.hostname + '"])');
  externalLinks.forEach((link) => {
    if (!link.getAttribute('rel')) {
      link.setAttribute('rel', 'noopener noreferrer');
      fixed.push(`Added rel="noopener noreferrer" to external link`);
    }
  });

  // Recommendations
  recommendations.push('Implement HTTPS enforcement');
  recommendations.push('Add security headers');
  recommendations.push('Optimize robots.txt');
  recommendations.push('Set up proper canonical URLs');
  recommendations.push('Validate structured data regularly');

  return { fixed, recommendations };
};

/**
 * Initialize all technical SEO optimizations
 */
export const initTechnicalSEO = (config: Partial<TechnicalSEOConfig> = {}): void => {
  const defaultConfig: TechnicalSEOConfig = {
    enforceHTTPS: true,
    securityHeaders: true,
    duplicateContentPrevention: true,
    crawlBudgetOptimization: true,
    canonicalURLs: true,
    robotsOptimization: true,
    ...config,
  };

  // Enforce HTTPS
  if (defaultConfig.enforceHTTPS) {
    enforceHTTPS();
  }

  // Manage canonical URLs
  if (defaultConfig.canonicalURLs) {
    autoManageCanonicalURLs();
  }

  // Optimize meta robots
  optimizeMetaRobots();

  // Auto-fix common issues
  const { fixed } = autoFixTechnicalSEO();
  
  if (fixed.length > 0) {
    console.log('Technical SEO auto-fixes applied:', fixed);
  }
};